import React from 'react';
import { Text } from '@hub/text';
import { Stack } from '@hub/stack';
import { H3 } from '@hub/heading';
import { CoreContainer } from '@hub/core-container';
import { Link, LinkBox, LinkOverlay } from '@hub/link';
import { Grid } from '@hub/grid';
import { Box } from '@hub/box';
import { Cluster } from '@hub/cluster';
import { useToken } from '@hub/design-system-base';
import { CloudinaryImage, IMAGE_SIZES_GRID_PRIMARY, IMAGE_SIZES_GRID_SECONDARY } from '../cloudinary-image';
import trackClickEvents, { categories } from '../analytics/trackEvents';
import { TrackBox } from '../../analytics-observer/track-box';
import { TrackLinkBox } from '../../analytics-observer/track-link-box';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MAX_PRIMARY_RETAILERS = 6;

/**
 * Example usage:

  <StoreGrid
    shopAllLink="https://placekitten.com/"
    primaryRetailers={[...new Array(6)].map(() => ({
      src: 'https://placekitten.com/500/500',
      image: {
        description: 'kitten',
        src: 'https://placekitten.com/500/500',
      },
    }))}
    secondaryRetailers={[...new Array(40)].map(() => ({
      src: 'https://placekitten.com/100/200',
      image: {
        src: 'https://placekitten.com/700/700',
        description: 'a cute cat',
      },
    }))}
  />
*/
export const StoreGrid = _ref => {
  var _callToAction$label;

  let {
    title,
    subtitle,
    retailers = [],
    secondaryRetailers = retailers === null || retailers === void 0 ? void 0 : retailers.slice(MAX_PRIMARY_RETAILERS),
    primaryRetailers = retailers === null || retailers === void 0 ? void 0 : retailers.slice(0, MAX_PRIMARY_RETAILERS),
    callToAction,
    as,
    clickEvent,
    enableH1Heading: enableH1Heading
  } = _ref;
  const TILE_SIZE_TOKEN = 'size-9';
  const SPACING_TOKEN = 'spacing-lg';
  const spacing = useToken('space', SPACING_TOKEN);
  const tileSize = useToken('sizes', TILE_SIZE_TOKEN);
  return /*#__PURE__*/_jsxs(CoreContainer, {
    as: as,
    "data-id": "store-grid_index_StoreGrid_CoreContainer",
    children: [/*#__PURE__*/_jsxs(Stack, {
      sx: {
        paddingBottom: 'spacing-sm'
      },
      "data-id": "store-grid_index_StoreGrid_CoreContainer_Stack",
      children: [title && /*#__PURE__*/_jsx(H3, {
        as: enableH1Heading ? 'h1' : 'h3',
        align: "center",
        "data-id": "store-grid_index_StoreGrid_H3",
        children: title
      }), subtitle && /*#__PURE__*/_jsx(Text, {
        align: "center",
        color: "quartz",
        "data-id": "store-grid_index_StoreGrid_Text",
        children: subtitle
      })]
    }), /*#__PURE__*/_jsxs(Stack, {
      gap: SPACING_TOKEN,
      sx: {
        overflow: 'hidden'
      },
      "data-id": "store-grid_index_StoreGrid_CoreContainer_Stack",
      children: [/*#__PURE__*/_jsx(Grid, {
        maxWidth: ['size-md', null, 'size-sm'],
        sx: {
          gridGap: ['spacing-sm', 'spacing-md']
        },
        role: "list",
        "data-id": "store-grid_index_StoreGrid_Stack_Grid",
        children: primaryRetailers.map(_ref2 => {
          let {
            logoUrl,
            title,
            url,
            as,
            index,
            viewItem
          } = _ref2;
          return /*#__PURE__*/_jsx(TrackBox, {
            onCreateImpressionWhenView: viewItem,
            "data-id": "store-grid_index_StoreGrid_TrackBox",
            children: /*#__PURE__*/_jsx(LinkBox, {
              role: "listitem",
              sx: {
                border: 'solid 1px',
                borderColor: 'ghost',
                padding: 'spacing-sm'
              },
              as: as,
              onClick: () => {
                if (clickEvent) {
                  clickEvent(index !== null && index !== void 0 ? index : 0);
                }

                trackClickEvents.clicked(categories.STORE_GRID, "Primary Retailer: ".concat(title));
              },
              "data-id": "store-grid_index_StoreGrid_TrackBox_LinkBox",
              children: /*#__PURE__*/_jsx(LinkOverlay, {
                href: url,
                "data-id": "store-grid_index_StoreGrid_LinkBox_LinkOverlay",
                children: /*#__PURE__*/_jsx(CloudinaryImage, {
                  ratio: "1",
                  imageSetOrImage: {
                    url: logoUrl,
                    alt: title,
                    type: 'SQUARE'
                  },
                  minWidth: "size-sm",
                  resizeMode: "pad",
                  sizes: IMAGE_SIZES_GRID_PRIMARY(primaryRetailers.length),
                  "data-id": "store-grid_index_StoreGrid_LinkOverlay_CloudinaryImage"
                })
              })
            })
          }, url);
        })
      }), secondaryRetailers.length > 0 && /*#__PURE__*/_jsx(Cluster, {
        minSize: TILE_SIZE_TOKEN,
        gap: SPACING_TOKEN,
        align: "center",
        justify: "center",
        sx: {
          marginTop: "calc(".concat(spacing, " / 2 * -1)"),
          maxHeight: "calc((".concat(tileSize, " * 2) + (").concat(spacing, " * 2))")
        },
        role: "list",
        "data-id": "store-grid_index_StoreGrid_Cluster",
        children: secondaryRetailers.map(_ref3 => {
          var _ref4, _alternativeLogoUrls$;

          let {
            alternativeLogoUrls,
            logoUrl,
            title,
            url,
            as,
            index,
            viewItem
          } = _ref3;
          return /*#__PURE__*/_jsx(Box, {
            sx: {
              display: 'flex',
              alignItems: 'center',
              borderColor: 'ghost'
            },
            role: "listitem",
            as: as,
            "data-id": "store-grid_index_StoreGrid_Box",
            children: /*#__PURE__*/_jsx(TrackLinkBox, {
              onCreateImpressionWhenView: viewItem,
              onClick: () => {
                if (clickEvent) {
                  clickEvent(index !== null && index !== void 0 ? index : 0);
                }

                trackClickEvents.clicked(categories.STORE_GRID, "Secondary Retailer: ".concat(title));
              },
              "data-id": "store-grid_index_StoreGrid_Box_TrackLinkBox",
              children: /*#__PURE__*/_jsx(LinkOverlay, {
                href: url,
                "data-id": "store-grid_index_StoreGrid_TrackLinkBox_LinkOverlay",
                children: /*#__PURE__*/_jsx(Box, {
                  sx: {
                    filter: 'grayscale(1) opacity(0.7)',
                    maxWidth: TILE_SIZE_TOKEN
                  },
                  "data-id": "store-grid_index_StoreGrid_LinkOverlay_Box",
                  children: /*#__PURE__*/_jsx(CloudinaryImage, {
                    ratio: "1",
                    imageSetOrImage: {
                      url: (_ref4 = (_alternativeLogoUrls$ = alternativeLogoUrls === null || alternativeLogoUrls === void 0 ? void 0 : alternativeLogoUrls.coloured) !== null && _alternativeLogoUrls$ !== void 0 ? _alternativeLogoUrls$ : alternativeLogoUrls === null || alternativeLogoUrls === void 0 ? void 0 : alternativeLogoUrls.transparent) !== null && _ref4 !== void 0 ? _ref4 : logoUrl,
                      alt: title,
                      type: 'SQUARE'
                    },
                    sizes: IMAGE_SIZES_GRID_SECONDARY,
                    minWidth: TILE_SIZE_TOKEN,
                    "data-id": "store-grid_index_StoreGrid_Box_CloudinaryImage"
                  })
                })
              })
            })
          }, url);
        })
      })]
    }), callToAction && /*#__PURE__*/_jsxs(Stack, {
      gap: "spacing-sm",
      sx: {
        marginTop: 'spacing-md'
      },
      "data-id": "store-grid_index_StoreGrid_Stack",
      children: [/*#__PURE__*/_jsx(Box, {
        "data-id": "store-grid_index_StoreGrid_Stack_Box"
      }), /*#__PURE__*/_jsx(Text, {
        align: "center",
        "data-id": "store-grid_index_StoreGrid_Stack_Text",
        children: /*#__PURE__*/_jsx(Link, {
          hasUnderline: true,
          href: callToAction.url,
          "data-id": "store-grid_index_StoreGrid_Text_Link",
          children: (_callToAction$label = callToAction.label) !== null && _callToAction$label !== void 0 ? _callToAction$label : 'Shop all stores'
        })
      })]
    })]
  });
};